import {MessarioAiBundle, MessarioAiSession} from '@lib/messarioAi/types';

export const MESSARIO_AI_SESSION_KEY_PREFIX = 'messario_ai_session:';

function storageKey(externalUserId: string) {
  return `${MESSARIO_AI_SESSION_KEY_PREFIX}${externalUserId}`;
}

function isAiBundle(value: unknown): value is MessarioAiBundle {
  if(!value || typeof value !== 'object') {
    return false;
  }

  const bundle = value as MessarioAiBundle;
  if(bundle.enabled === false) {
    return true;
  }

  if(bundle.enabled !== true) {
    return false;
  }

  const revisionOk = bundle.variable_definitions_revision === undefined ||
    typeof bundle.variable_definitions_revision === 'number';

  return typeof bundle.prompt_revision === 'number' &&
    (bundle.compiled_at === null || typeof bundle.compiled_at === 'string') &&
    typeof bundle.model === 'string' &&
    typeof bundle.history_limit === 'number' &&
    (bundle.folder_id === null || typeof bundle.folder_id === 'number') &&
    !!bundle.features &&
    typeof bundle.features.one_shot_instruction === 'boolean' &&
    typeof bundle.features.draft_only === 'boolean' &&
    revisionOk;
}

function isSession(value: unknown): value is MessarioAiSession {
  if(!value || typeof value !== 'object') {
    return false;
  }

  const session = value as MessarioAiSession;
  return typeof session.account_id === 'string' &&
    !!session.account_id &&
    typeof session.panel_user_id === 'string' &&
    !!session.panel_user_id &&
    typeof session.external_user_id === 'string' &&
    !!session.external_user_id &&
    isAiBundle(session.ai_bundle);
}

function metadataOnlySession(session: MessarioAiSession): MessarioAiSession {
  if(!session.ai_bundle.enabled) {
    return {
      account_id: session.account_id,
      panel_user_id: session.panel_user_id,
      external_user_id: session.external_user_id,
      ai_bundle: {enabled: false}
    };
  }

  const bundle = session.ai_bundle;
  const metadata: MessarioAiBundle = {
    enabled: true,
    prompt_revision: bundle.prompt_revision,
    compiled_at: bundle.compiled_at,
    model: bundle.model,
    history_limit: bundle.history_limit,
    folder_id: bundle.folder_id,
    features: {
      one_shot_instruction: bundle.features.one_shot_instruction,
      draft_only: bundle.features.draft_only
    }
  };
  if(typeof bundle.variable_definitions_revision === 'number') {
    metadata.variable_definitions_revision = bundle.variable_definitions_revision;
  }
  if(bundle.generate_transport === 'server' || bundle.generate_transport === 'local') {
    metadata.generate_transport = bundle.generate_transport;
  }

  return {
    account_id: session.account_id,
    panel_user_id: session.panel_user_id,
    external_user_id: session.external_user_id,
    ai_bundle: metadata
  };
}

export function saveMessarioAiSession(
  session: MessarioAiSession,
  storage: Storage = window.sessionStorage
) {
  if(!session.external_user_id || !isSession(session)) {
    return;
  }

  storage.setItem(storageKey(session.external_user_id), JSON.stringify(metadataOnlySession(session)));
}

export function clearMessarioAiSession(
  externalUserId: string,
  storage: Storage = window.sessionStorage
) {
  storage.removeItem(storageKey(externalUserId));
}

export function getMessarioAiSession(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiSession | undefined {
  const value = storage.getItem(storageKey(externalUserId));
  if(!value) {
    return undefined;
  }

  try {
    const session = JSON.parse(value) as unknown;
    if(!isSession(session) || session.external_user_id !== externalUserId) {
      clearMessarioAiSession(externalUserId, storage);
      return undefined;
    }

    return session;
  } catch(err) {
    clearMessarioAiSession(externalUserId, storage);
    return undefined;
  }
}

export function getMessarioAiBundle(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiBundle | undefined {
  return getMessarioAiSession(externalUserId, storage)?.ai_bundle;
}

export function isMessarioAiEnabledForUser(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): boolean {
  const bundle = getMessarioAiBundle(externalUserId, storage);
  return !!bundle?.enabled;
}
